import type { FavoriteView, FavoriteViewFolder } from '@/api/favorites'
import type { ModalProps } from '@/components/Modal/Modal'
import { VpIcon } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/Modal/Modal'

export type FavoriteViewsModalState
  = { type: 'delete-folder', folder: FavoriteViewFolder }
    | { type: 'delete-view', view: FavoriteView }
    | { type: 'share', view: FavoriteView, url: string }

interface FavoriteViewsManagementModalProps {
  activeModal: FavoriteViewsModalState | null
  isOpen: boolean
  isDeleteFolderPending: boolean
  isDeleteViewPending: boolean
  onClose: () => void
  onCancel: () => void
  onValidate: () => void
}

type ModalPresentation = Pick<
  ModalProps,
  | 'dataTestid'
  | 'title'
  | 'validationDisabled'
  | 'validationLabel'
  | 'validationVariant'
  | 'children'
>

export function FavoriteViewsManagementModal({
  activeModal,
  isOpen,
  isDeleteFolderPending,
  isDeleteViewPending,
  onClose,
  onCancel,
  onValidate,
}: Readonly<FavoriteViewsManagementModalProps>) {
  const { t } = useTranslation()

  const getModalPresentation = (): ModalPresentation => {
    const shareLinkProps = {
      dataTestid: 'favorite-view-share-link',
      title: t('favorite-views.management.share-view-title'),
      validationDisabled: false,
      validationLabel: (
        <>
          <VpIcon name="link" />
          {t('favorite-views.management.copy-link')}
        </>
      ),
      validationVariant: 'primary' as const,
    }

    switch (activeModal?.type) {
      case 'delete-folder':
        return {
          dataTestid: 'favorite-view-folder-delete-confirmation',
          title: t('favorite-views.management.delete-folder-title'),
          validationDisabled: isDeleteFolderPending,
          validationLabel: t('favorite-views.management.delete'),
          validationVariant: 'negative',
          children: (
            <div className="favorite-views-page__delete-confirmation">
              <p>{t('favorite-views.management.delete-folder-confirmation', { name: activeModal.folder.name })}</p>
              <p>{t('favorite-views.management.delete-folder-confirmation-warning', { count: activeModal.folder.viewCount })}</p>
            </div>
          ),
        }
      case 'delete-view':
        return {
          dataTestid: 'favorite-view-delete-confirmation',
          title: t('favorite-views.management.delete-view-title'),
          validationDisabled: isDeleteViewPending,
          validationLabel: t('favorite-views.management.delete-view'),
          validationVariant: 'negative',
          children: (
            <div className="favorite-views-page__delete-confirmation">
              <p>{t('favorite-views.management.delete-view-description', { title: activeModal.view.title })}</p>
              <p>{t('favorite-views.management.delete-view-warning')}</p>
            </div>
          ),
        }
      case 'share':
        return {
          ...shareLinkProps,
          children: (
            <div className="favorite-views-page__share-link">
              <p>{t('favorite-views.management.share-view-description')}</p>
              <span
                data-testid="favorite-view-share-url"
                aria-label={t('favorite-views.management.share-link-label')}
              >
                {activeModal.url}
              </span>
            </div>
          ),
        }
      default:
        return {
          dataTestid: 'null',
          children: null,
          title: null,
        }
    }
  }

  const modalPresentation = getModalPresentation()

  return (
    <Modal
      {...modalPresentation}
      width="60vw"
      cancelLabel={t('cancel')}
      isOpen={isOpen}
      onClose={onClose}
      cancelAction={onCancel}
      validationAction={onValidate}
    />
  )
}
